import { useState, memo, FC } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Box from '@mui/material/Box'
import CreateTemplatePanel from '../CreateTemplatePanel'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '../../../atoms/CircularProgress'
import { useActiveTemplate } from '../../../providers/ActiveTemplateProvider'
import TemplatePanel from '../TemplatePanel'
import useCallbackDebounced from '../../../../hooks/shared/useCallbackDebounced'
import useStableCallback from '../../../../hooks/shared/useStableCallback'

const TemplateDialogPanel: FC<{ onSave: () => void; onClose: () => void }> = ({
  onSave,
  onClose,
}) => {
  const { activeTemplate, loading } = useActiveTemplate()
  const [editorModeEnabled, setEditorModeEnabled] = useState<boolean>()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [defaultTemplateJSON, setDefaultTemplateJSON] = useState<any | null>()
  const onTemplateSelected = useCallbackDebounced((args?: { defaultTemplateJSON?: any | null }) => {
    setDefaultTemplateJSON(args?.defaultTemplateJSON)
    setEditorModeEnabled(true)
  }, [])
  const wrappedOnClose = useStableCallback(() => {
    if (activeTemplate || !editorModeEnabled) {
      onClose()
    } else {
      setDefaultTemplateJSON(undefined)
      setEditorModeEnabled(undefined)
    }
  })
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          overflow: 'scroll',
          ...(!activeTemplate &&
            !defaultTemplateJSON &&
            !editorModeEnabled && {
              paddingTop: '70px',
              paddingBottom: '70px',
            }),
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <CircularProgress delay={200} />
          </Box>
        )}
        {!loading && !activeTemplate && !defaultTemplateJSON && !editorModeEnabled && (
          <CreateTemplatePanel onTemplateSelected={onTemplateSelected} />
        )}
        {!loading && (activeTemplate || defaultTemplateJSON || editorModeEnabled) && (
          <TemplatePanel
            onClose={wrappedOnClose}
            onSave={onSave}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            defaultTemplateJSON={defaultTemplateJSON}
          />
        )}
      </Box>
      {!activeTemplate && !defaultTemplateJSON && !editorModeEnabled && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              display: 'flex',
              padding: 2,
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Box
              sx={theme => ({
                position: 'absolute',
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
                [theme.breakpoints.down('sm')]: {
                  zIndex: 1,
                  backgroundColor: 'pageBackground',
                  maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,0))',
                },
              })}
            />
            <IconButton
              onClick={onClose}
              sx={{
                backgroundColor: 'white',
                pointerEvents: 'visible',
                zIndex: 1,
                '&:hover': {
                  backgroundColor: 'white',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default memo(TemplateDialogPanel)
